import React, { useState, useEffect } from "react";

import { Typography } from "@packages/ds";
import momentjs from "moment";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { Button, ButtonWrapper, BackLink, IconControl } from "ds/Button";
import Checkbox from "ds/Checkbox";
import Dialog from "ds/Dialog";
import Input from "ds/Input";
import Loading from "ds/PageLoading";
import Select from "ds/Select";
import * as Sticker from "ds/Sticker";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { useLoadInvitationsAndAccesses } from "Reducers/access/access.hooks";
import { deleteInvitationThunk } from "Reducers/access/invitation/thunks/deleteInvitation.thunk";
import { environmentsAsArraySelector } from "Reducers/environment";
import { useLoggedInMember } from "Reducers/organization/loggedInMember/hooks/useLoggedInMember";
import { selectHasManagementAccess } from "Reducers/project/project";
import useOrganizationFromURL from "src/organization/pages/settings/pages/members/hooks/useOrganizationFromURL";
import {
  singleInvitationSelector,
  statusSelector
} from "src/reducers/access/invitation/invitation.selectors";
import { resendInvitationThunk } from "src/reducers/access/invitation/thunks/resendInvitation.thunk";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./InvitationDetails.styles";
import { ResendModal } from "./ResendModal";
import { RevokeModal } from "./RevokeModal";

const Invitation = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const invitation = useSelectorWithUrlParams(singleInvitationSelector);
  const loading = useLoadInvitationsAndAccesses();

  const status = useAppSelector(statusSelector);
  const { projectId = "", organizationId = "" } = useDecodedParams<{
    organizationId: string;
    projectId: string;
  }>();

  const organization = useOrganizationFromURL(organizationId);
  const { load: loadLoggedInMember } = useLoggedInMember(
    organization?.id || ""
  );

  useEffect(() => {
    loadLoggedInMember();
  }, [loadLoggedInMember]);

  const manageAccessEnabled = useSelectorWithUrlParams(
    selectHasManagementAccess
  );

  const environments = useSelectorWithUrlParams(environmentsAsArraySelector);

  const accessURL = `/${organizationId}/${projectId}/-/settings/access`;
  const [resendConfirmationModalIsOpen, setResendConfirmationModalIsOpen] =
    useState(false);
  const [revokeConfirmationModalIsOpen, setRevokeConfirmationModalIsOpen] =
    useState(false);

  const [disabledRevokeButton, setDisabledRevokeButton] = useState(false);
  const onResend = () => {
    setResendConfirmationModalIsOpen(true);
  };
  const onRevoke = () => {
    setRevokeConfirmationModalIsOpen(true);
  };

  const showProjectPermissions = invitation?.role === "admin";
  const showEnvironmentPermissions = !showProjectPermissions;

  const getEnvironmentsByType = environmentType => {
    return (
      environments?.filter(e => e.type === environmentType).map(e => e.name) ||
      []
    );
  };

  const resendInvitation = () => {
    if (!invitation) throw new Error("Invitation not found");
    dispatch(
      resendInvitationThunk({
        organizationId,
        projectId,
        email: invitation.email,
        environmentTypes: invitation.environments,
        adminRights: invitation.role === "admin",
        invitation
      })
    );
    setResendConfirmationModalIsOpen(false);
    setDisabledRevokeButton(true);
  };

  const revokeInvitation = async () => {
    if (!invitation) throw new Error("Invitation not found");
    await dispatch(
      deleteInvitationThunk({
        organizationId,
        projectId,
        invitation
      })
    );
    navigate(`../../access`);
    setRevokeConfirmationModalIsOpen(false);
  };

  if (loading) {
    return <Loading />;
  }

  if (!invitation) {
    throw new Error("no-invitation");
  }

  return (
    <section>
      <BackLink from="access" to={accessURL} capitalized={false} />
      <Typography.Title tag="h3">
        {intl.formatMessage({ id: "invitation.details.pending" })}
      </Typography.Title>

      <S.Container>
        <Sticker.Root
          priority="information"
          variant={manageAccessEnabled ? "button" : undefined}
        >
          <Sticker.Icon priority="information" />
          <Sticker.Text textStyle="information">
            {intl.formatMessage(
              { id: "invitation.info" },
              {
                name: invitation?.owner?.display_name,
                date: momentjs(invitation.created_at).format("MMMM D, YYYY")
              }
            )}
          </Sticker.Text>
          {manageAccessEnabled ? (
            status === "resent" ? (
              <span className="link-style">
                {intl.formatMessage({ id: "invitation.resend_success" })}
              </span>
            ) : (
              <Button onClick={onResend} analyticId="invitation.resend">
                {intl.formatMessage({ id: "invitation.resend" })}
              </Button>
            )
          ) : null}
        </Sticker.Root>

        <section style={{ marginTop: "16px" }}>
          <Input
            id="email"
            label={intl.formatMessage({ id: "invitation.details.email" })}
            required
            disabled
            placeholder={invitation.email}
            isValid={true}
            analyticId="invitation.details.email"
          />
          {showProjectPermissions && (
            <>
              <Typography.Title tag="h4" style={{ margin: "16px 0" }}>
                {intl.formatMessage({
                  id: "access.project.permissions"
                })}
              </Typography.Title>
              <Checkbox
                label={`${intl.formatMessage({ id: "access.project_admin" })}`}
                checked={invitation.role === "admin"}
                disabled={true}
                aria-disabled
                forId="super-user"
                id="access.project_admin"
              />
            </>
          )}
          {showEnvironmentPermissions && (
            <>
              <S.EnvironmentPermissionsWrapper>
                <Typography.Title
                  tag="h4"
                  style={{ margin: "16px 16px 16px 0" }}
                >
                  {intl.formatMessage({
                    id: "invitation.environment_types.permission"
                  })}
                </Typography.Title>
                <Dialog
                  align="start"
                  description={intl.formatMessage({
                    id: "access.environment_types.description"
                  })}
                  analyticId="access.environment_types.description"
                  link={
                    <a
                      href={intl.formatMessage({
                        id: "links.documentation.users"
                      })}
                    >
                      {intl.formatMessage({ id: "learnmore" })}
                    </a>
                  }
                  trigger={
                    <IconControl>
                      <S.InfoIcon />
                    </IconControl>
                  }
                />
              </S.EnvironmentPermissionsWrapper>
              <S.EnvironmentTypesTable>
                <S.EnvironmentTableHeaders>
                  <S.EnvironmentTableHeader width="20%">
                    <Typography.Label>
                      {intl.formatMessage({
                        id: "invitation.environment_types"
                      })}
                    </Typography.Label>
                  </S.EnvironmentTableHeader>
                  <S.EnvironmentTableHeader width="30%">
                    <Typography.Label>
                      {intl.formatMessage({
                        id: "invitation.environment.type"
                      })}
                    </Typography.Label>
                  </S.EnvironmentTableHeader>
                  <S.EnvironmentTableHeader width="45%">
                    <Typography.Label>
                      {intl.formatMessage({
                        id: "invitation.table.environments"
                      })}
                    </Typography.Label>
                  </S.EnvironmentTableHeader>
                </S.EnvironmentTableHeaders>
                {invitation.environments.map(environment => (
                  <S.EnvironmentTableRow key={environment.type}>
                    <S.EnvironmentTableCell width="20%">
                      <S.EbonyLabel>
                        {capitalize(environment.type)}
                      </S.EbonyLabel>
                    </S.EnvironmentTableCell>
                    <S.EnvironmentTableCell width="30%">
                      <div style={{ marginRight: "40px" }}>
                        <Select
                          id="access.options.no_access"
                          placeholder={
                            environment.role === "no-access"
                              ? intl.formatMessage({
                                  id: "access.options.no_access"
                                })
                              : capitalize(environment.role)
                          }
                          isDisabled
                        />
                      </div>
                    </S.EnvironmentTableCell>
                    <S.EnvironmentTableCell width="45%">
                      <S.EbonyLightText size="small">
                        {getEnvironmentsByType(environment.type).join(", ")}
                      </S.EbonyLightText>
                    </S.EnvironmentTableCell>
                  </S.EnvironmentTableRow>
                ))}
              </S.EnvironmentTypesTable>
            </>
          )}
        </section>
        {manageAccessEnabled && (
          <ButtonWrapper justifyContent="end" style={{ marginTop: "40px" }}>
            <Button
              analyticId="invitation.revoke"
              id={`invitation-revoke-${invitation.id}`}
              variant="outline"
              onClick={onRevoke}
              disabled={disabledRevokeButton}
            >
              {intl.formatMessage({ id: "invitation.revoke" })}
            </Button>
          </ButtonWrapper>
        )}
      </S.Container>
      <ResendModal
        isOpen={resendConfirmationModalIsOpen}
        setIsOpen={setResendConfirmationModalIsOpen}
        email={invitation.email}
        onAccept={resendInvitation}
      />
      <RevokeModal
        isOpen={revokeConfirmationModalIsOpen}
        setIsOpen={setRevokeConfirmationModalIsOpen}
        email={invitation.email}
        onAccept={revokeInvitation}
      />
    </section>
  );
};

export default Invitation;
